/**
 * Declarative RBAC Permission Gate.
 * Wraps children and only renders them if the user has the required permission.
 * 
 * Usage:
 *   <Can permission={PERMISSIONS.DATASOURCES_CREATE}>
 *     <Button>Create</Button>
 *   </Can>
 * 
 *   <Can permission={PERMISSIONS.USERS_DELETE} fallback={<span>No access</span>}>
 *     <DeleteButton />
 *   </Can>
 */
import React from 'react';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface CanProps {
  /** The permission string required (e.g., 'datasources:create') */
  permission: string;
  /** Optional fallback UI when permission is denied */
  fallback?: React.ReactNode;
  /** Children rendered when permission is granted */
  children: React.ReactNode;
}

export const Can: React.FC<CanProps> = ({ permission, fallback = null, children }) => {
  const { user } = useAuth();

  if (!hasPermission(user, permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default Can;
